import React, { type Dispatch, type SetStateAction } from "react"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { Grid } from "@mui/material"
import { Button, type InputOptionValue } from "@synapse-analytics/synapse-ui"

import type { FilterOptions } from "../filters/MenuFilter"
import { SearchSortFilter } from "../form/SearchSortFilter"

import styles from "./KonanSubHeader.module.scss"

type Props = {
  setAction?: Dispatch<SetStateAction<boolean>>
  title?: string
  searchValue: string | undefined
  setSearchValue: React.Dispatch<React.SetStateAction<string>>
  sortValue?: string | undefined
  setSortValue?: React.Dispatch<React.SetStateAction<string>>
  filterValue?: string[] | FilterOptions | null
  setFilterValue?: React.Dispatch<React.SetStateAction<string[]>> | React.Dispatch<React.SetStateAction<FilterOptions>>
  containerType:
    | "Project"
    | "Model"
    | "Retraining"
    | "Configuration"
    | "Script"
    | "File"
    | "Calculator"
    | "Ruleset"
    | "Taglist"
    | "Programs"
    | "Scorecard"
    | "Scorecard Set"
    | "Marketplace"
  filterOptions?: string[] | FilterOptions
  sortOptions?: InputOptionValue[]
  isBtnDisabled?: boolean
  disabledBtnTooltip?: string
  actions?: React.ReactElement[]
  showButton?: boolean
  isDebouncedInput?: boolean
  useMenuFilter?: boolean
}

/**
 * section header component
 * @param  {string} title section title
 * @param  {Dispatch} setAction section Action
 * Values passed to Search sort filter component
 * @param  {string} searchValue
 * @param  {React.Dispatch} setSearchValue
 * @param  {string} onSortValueChange
 * @param  {React.Dispatch} setSortValue
 * @param  {string} filterValue
 * @param  {React.Dispatch} setFilterValue
 * @param  {"Project" | "Model" | "Retraining"} containerType
 * @param  {string[]} filterOptions
 * @param  {string[]} sortOptions
 * @param  {boolean} isBtnDisabled
 * @param  {string} disabledBtnTooltip
 * @param  {boolean} useMenuFilter indicates which filter to use
 * @return {React.ReactElement}
 */
export function KonanSubHeader(props: Readonly<Props>): React.ReactElement {
  const {
    setAction,
    title,
    sortValue,
    containerType,
    sortOptions,
    setSearchValue,
    filterValue,
    setFilterValue,
    setSortValue,
    searchValue,
    filterOptions,
    isBtnDisabled = false,
    showButton = true,
    disabledBtnTooltip,
    actions,
    isDebouncedInput,
    useMenuFilter = false,
  } = props

  return (
    <Grid
      direction="row"
      container
      display="flex"
      spacing={1}
      sx={{
        flexDirection: {
          xs: "column", // column direction for xs to lg
          md: "row", // row direction for md and above
        },
        justifyContent: {
          xs: "flex-start", // flex-start for xs to md
          md: "space-between", // space-between for md and above
        },
        alignItems: {
          xs: "flex-start", // flex-start for xs to md
          md: "end", // center for md and above
        },
      }}
    >
      <Grid item className={styles.ssfContainer}>
        <SearchSortFilter
          searchValue={searchValue}
          onSearchValueChange={setSearchValue}
          sortValue={sortValue}
          onSortValueChange={setSortValue}
          filterValue={filterValue}
          onFilterValueChange={setFilterValue}
          containerType={containerType}
          sortOptions={sortOptions}
          filterOptions={filterOptions}
          isDebouncedInput={isDebouncedInput}
          useMenuFilter={useMenuFilter}
        />
      </Grid>

      {((actions && actions.length > 0) || setAction) && (
        <Grid item display="flex" gap={1}>
          {actions && actions?.length > 0
            ? actions?.map((action, index) => {
                return (
                  <Grid item key={index}>
                    {action}
                  </Grid>
                )
              })
            : showButton && (
                <Button
                  onClick={() => setAction?.(true)}
                  variant="primary"
                  disabled={isBtnDisabled}
                  className={styles.subHeaderBtn}
                  startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
                  id="sub_header_btn"
                  size="regular"
                  tooltip={disabledBtnTooltip}
                >
                  {title}
                </Button>
              )}
        </Grid>
      )}
    </Grid>
  )
}
